<template>
    <el-checkbox-group
        v-model="itemSelectedCityIds"
        @change="onChangeSelected"
    >
        <el-checkbox
            v-for="item in allOptionCityList"
            :key="item.value"
            :disabled="isCityDisabled(item.value)"
            :label="item.value"
            @click.native="handleCheckboxClick(item.value)"
        >
            {{ item.label }}
        </el-checkbox>
    </el-checkbox-group>
</template>

<script>
// 整体逻辑：一个城市无法被用在多个 item，每个 item 下的城市都是互斥的。
/**
 * 全量城市列表：allCityList
 * 全部已选中的城市 itemSelectedCityIds = (用户新选择的 selectedIds) + (已存在的城市：existedIds 需要被禁用 disabled)
 * 可选择的城市：publicIds
 */

import { getBaseModel } from '../baseModel';

export default {
    props: {
        existedIds: { // 草稿中对应的已存在城市 id 列表，只是初始化一次，进入之后就不再被修改，在视图中表现为 disabled。
            type: Array,
            default: () => [],
        },
        publicIds: { // 本类型（比如配方单、保质期等）下，公共可追加选择的列表
            type: Array,
            default: () => [],
        },
        selectedIds: { // 本次 新提报的城市
            type: Array,
            default: () => [],
        },
        uuid: {
            type: String,
            required: true,
        },
        allCityList: {
            type: Array,
            default: () => [], // {label: '', value: ''}
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        preSkuId: {
            type: String,
            default: '',
        },
        // 按城市分组的保质期规则映射
        guaranteePeriodRuleMap: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            // 展示的所有列表项的集合：被勾选的：草稿中本来已经存在的 + 本local次操作新增的；未被勾选的：public 的
            itemSelectedCityIds: [], // 绑定到 select 上的  model，包含所有 被勾选的列表：即包含初始的 existedIds，又包含后续用户交互新增的
        };
    },
    computed: {
        allOptionCityList() { // 即 itemSelectedCityIds + publicIds
            // 顺序和 原始 city 列表保持一致
            const targetList = [...new Set([...this.itemSelectedCityIds, ...this.publicIds])].map((id) => +id);
            return this.allCityList.filter((item) => targetList.includes(item.value));
        },
        // 是否存在有效的城市，如果不存在，需要将这个item清理
        hasEffectiveIds() {
            return [...(this.publicIds || []), ...(this.existedIds || []), ...(this.selectedIds || [])].length > 0;
        },
        // pb 档案城市列表
        pdArchiveCityList() {
            if (!this.preSkuId) {
                return [];
            }
            const baseModel = getBaseModel();
            const citySkuManageSection = baseModel.models.citySkuManageSection || baseModel.data?.citySkuManageSection;
            const citySkuManageInfo = citySkuManageSection?.citySkuManageInfo || [];
            return citySkuManageInfo.map((item) => item.cityId);
        },
        // 【新增】判断当前分组是否有默认保质期（根据 publicIds 中的城市判断）
        groupHasDefaultGuaranteePeriod() {
            // 获取已选城市中的新增城市（不包括 existedIds）
            const selectedCityIds = this.itemSelectedCityIds.filter((id) => !this.existedIds.includes(id));

            // 如果有已选城市，则根据已选城市判断
            if (selectedCityIds.length > 0) {
                // 检查已选城市中是否有任何城市有默认保质期
                return selectedCityIds.some((cityId) => {
                    const rule = this.guaranteePeriodRuleMap[cityId];
                    return rule && rule.guaranteePeriod;
                });
            }

            // 如果没有已选城市，则根据 publicIds 判断
            // 这时分组是空的，需要根据可选城市来判断分组的属性
            const allAvailableCityIds = [...new Set([...this.publicIds])];

            if (allAvailableCityIds.length === 0) {
                return false;
            }

            // 检查所有可选城市中是否有任何城市有默认保质期
            return allAvailableCityIds.some((cityId) => {
                const rule = this.guaranteePeriodRuleMap[cityId];
                return rule && rule.guaranteePeriod;
            });
        },
    },
    watch: {
        existedIds(newVal) {
            this.itemSelectedCityIds = [...newVal, ...this.selectedIds];
        },
        selectedIds(newVal) {
            this.itemSelectedCityIds = [...newVal, ...this.existedIds];
        },
        // 当子组件已选择城市变化，需要通知父组件，来更新公共可选择的城市
        itemSelectedCityIds() {
            this.$emit('selected-ids-change');
        },
    },
    created() {
        if (this.existedIds && this.selectedIds) {
            this.itemSelectedCityIds = [...this.existedIds, ...this.selectedIds];
        }
    },
    methods: {
        // 判断城市是否是 pb 档案带过来的
        isPdArchiveCity(cityId) {
            if (!this.preSkuId || !this.pdArchiveCityList.length) {
                return false;
            }
            return this.pdArchiveCityList.includes(cityId);
        },
        // 判断城市是否应该被禁用
        isCityDisabled(cityId) {
            // 原有的禁用逻辑：disabled prop 或 existedIds 中的城市
            if (this.disabled || this.existedIds.includes(cityId) || this.isGuaranteePeriodInconsistent(cityId)) {
                return true;
            }
            // 如果有 preSkuId，且城市是 pb 档案带过来的，则禁用
            if (this.preSkuId && this.isPdArchiveCity(cityId)) {
                return true;
            }
            // 其他情况不禁用（包括新增城市）
            return false;
        },
        // 处理复选框点击事件，当城市保质期不一致时显示提示
        handleCheckboxClick(cityId) {
            // 【优化】只需检查保质期不一致的情况
            // 已存在城市和组件禁用状态已在模板中处理，不会触发此方法
            if (!this.isGuaranteePeriodInconsistent(cityId)) {
                return;
            }

            // 只有保质期不一致时才显示提示
            this.$message.warning('当前城市与已选中城市默认保质期不一致，请先点击增加保质期后，再编辑保质期信息');
        },
        onChangeSelected(val) {
            // 检查是否有新增的城市，如果新增的城市建议保质期不一致，则显示提示并撤销选择
            const newSelectedIds = [...new Set(val)].filter((id) => !this.existedIds.includes(id));
            const previousSelectedIds = this.itemSelectedCityIds.filter((id) => !this.existedIds.includes(id));

            // 找出新增的城市
            const addedCityIds = newSelectedIds.filter((id) => !previousSelectedIds.includes(id));

            // 【修改】只有当 guaranteePeriodRuleMap 存在且有数据时，才进行保质期一致性检查
            if (addedCityIds.length > 0 && previousSelectedIds.length > 0 && this.guaranteePeriodRuleMap && Object.keys(this.guaranteePeriodRuleMap).length > 0) {
                const firstSelectedCityId = previousSelectedIds[0];
                const firstSelectedCityRule = this.guaranteePeriodRuleMap[firstSelectedCityId];

                const hasInconsistentCity = addedCityIds.some((addedCityId) => {
                    const addedCityRule = this.guaranteePeriodRuleMap[addedCityId];

                    if (firstSelectedCityRule && addedCityRule) {
                        const isConsistent = addedCityRule.guaranteePeriod === firstSelectedCityRule.guaranteePeriod
                            && addedCityRule.guaranteePeriodType === firstSelectedCityRule.guaranteePeriodType;

                        if (!isConsistent) {
                            return true;
                        }
                    }
                    return false;
                });

                if (hasInconsistentCity) {
                    // 【修复】撤销选择时也需要发送事件，保持状态同步
                    this.itemSelectedCityIds = previousSelectedIds.concat(this.existedIds);

                    // 【修复】发送事件，通知父组件状态变化
                    this.$emit('update:selected:city', { uuid: this.uuid, selectCityIds: previousSelectedIds });

                    this.$message.warning('当前城市与已选中城市默认保质期不一致，请先点击增加保质期后，再编辑保质期信息');
                    return;
                }
            }

            this.$emit('update:selected:city', { uuid: this.uuid, selectCityIds: newSelectedIds });
        },
        // 检查城市的保质期规则是否与已选城市一致
        isGuaranteePeriodInconsistent(cityId) {
            // 如果没有保质期规则映射，则不禁用
            if (!this.guaranteePeriodRuleMap || Object.keys(this.guaranteePeriodRuleMap).length === 0) {
                return false;
            }

            // 如果当前城市已被选中，则不禁用（已选中的城市不应该被禁用）
            if (this.itemSelectedCityIds.includes(cityId)) {
                return false;
            }

            // 获取当前城市的默认保质期规则
            const currentCityRule = this.guaranteePeriodRuleMap[cityId];

            // 获取已选城市中的新增城市（不包括 existedIds）
            const selectedCityIds = this.itemSelectedCityIds.filter((id) => !this.existedIds.includes(id));

            // 【修改】如果没有已选城市（新增分组），需要根据当前分组是否有默认保质期来判断
            if (selectedCityIds.length === 0) {
                // 如果当前分组有默认保质期，则禁用没有默认保质期的城市
                if (this.groupHasDefaultGuaranteePeriod) {
                    // 当前分组有默认保质期，禁用没有默认保质期的城市
                    if (!currentCityRule || !currentCityRule.guaranteePeriod) {
                        return true;
                    }
                    // 允许选择有默认保质期的城市
                    return false;
                } else {
                    // 当前分组没有默认保质期，禁用有默认保质期的城市
                    if (currentCityRule && currentCityRule.guaranteePeriod) {
                        return true;
                    }
                    // 允许选择没有默认保质期的城市
                    return false;
                }
            }

            // 获取已选城市中有默认保质期的城市，作为参考
            const selectedCitiesWithRule = selectedCityIds.filter((id) => {
                const rule = this.guaranteePeriodRuleMap[id];
                return rule && rule.guaranteePeriod;
            });

            // 【修复】如果已选城市中没有有默认保质期的城市
            if (selectedCitiesWithRule.length === 0) {
                // 但当前城市有默认保质期，则禁用（不能混合有和无）
                if (currentCityRule && currentCityRule.guaranteePeriod) {
                    return true;
                }
                // 如果当前城市也没有默认保质期，则不禁用
                return false;
            }

            // 获取已选城市中第一个有默认保质期的城市的规则
            const firstSelectedCityRule = this.guaranteePeriodRuleMap[selectedCitiesWithRule[0]];

            // 【修改】如果当前城市没有默认保质期，则禁用
            // 因为已选城市有默认保质期，不能混合
            if (!currentCityRule || !currentCityRule.guaranteePeriod) {
                return true;
            }

            // 比较保质期规则是否一致（都是默认保质期）
            const isConsistent = currentCityRule.guaranteePeriod === firstSelectedCityRule.guaranteePeriod
                && currentCityRule.guaranteePeriodType === firstSelectedCityRule.guaranteePeriodType;

            // 如果不一致，则禁用
            return !isConsistent;
        },
    },
};
</script>
